import { Directive, ElementRef, AfterViewInit, OnDestroy, Renderer2 } from '@angular/core';

@Directive({
  selector: '[ubsVerticalTabs]'
})
export class VerticalTabsDirective implements AfterViewInit, OnDestroy {
  private mutationObserver?: MutationObserver;

  constructor(
    private el: ElementRef,
    private renderer: Renderer2
  ) {}

  ngAfterViewInit(): void {
    // Wait for Nebular to fully render the tab structure
    setTimeout(() => {
      this.transformToVertical();
      this.setupMutationObserver();
    }, 0);
  }

  ngOnDestroy(): void {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
  }

  private transformToVertical(): void {
    const tabset = this.el.nativeElement;
    
    console.log('Transforming tabset:', tabset);
    
    // Add vertical tabs class to the main container
    this.renderer.addClass(tabset, 'vertical-tabs');
    console.log('Added vertical-tabs class, classes now:', tabset.className);
    
    // Find the tab list (headers)
    const tabList = tabset.querySelector('.tabset') || 
                   tabset.querySelector('nb-tab-list') ||
                   tabset.querySelector('.tab-list') ||
                   tabset.querySelector('[role="tablist"]');
                   
    // Find all nb-tab elements (content containers)
    const tabElements = tabset.querySelectorAll('nb-tab');
    
    console.log('Found tabList:', tabList);
    console.log('Found tab elements:', tabElements.length);
    
    if (!tabList || tabElements.length === 0) {
      console.warn('VerticalTabsDirective: Could not find required tab elements');
      // Try again after a longer delay
      setTimeout(() => this.transformToVertical(), 100);
      return;
    }

    // Apply CSS-only transformation without moving DOM elements
    this.applyVerticalStyles(tabset, tabList, tabElements);
  }

  private applyVerticalStyles(
    tabset: HTMLElement, 
    tabList: HTMLElement, 
    tabElements: NodeListOf<Element>
  ): void {
    // Main tabset container - use flexbox to arrange tabs and content side by side
    this.renderer.setStyle(tabset, 'display', 'flex');
    this.renderer.setStyle(tabset, 'flex-direction', 'row');
    this.renderer.setStyle(tabset, 'align-items', 'stretch');
    this.renderer.setStyle(tabset, 'height', '100%');
    this.renderer.setStyle(tabset, 'min-height', '400px');

    // Tab headers container - arrange vertically on the left
    this.renderer.setStyle(tabList, 'display', 'flex');
    this.renderer.setStyle(tabList, 'flex-direction', 'column');
    this.renderer.setStyle(tabList, 'flex-shrink', '0');
    this.renderer.setStyle(tabList, 'min-width', '200px');
    this.renderer.setStyle(tabList, 'max-width', '300px');
    this.renderer.setStyle(tabList, 'border-right', '1px solid var(--separator-color, #edf1f7)');
    this.renderer.setStyle(tabList, 'border-bottom', 'none');
    this.renderer.setStyle(tabList, 'background-color', 'var(--tabs-header-bg, #f7f9fc)');
    this.renderer.setStyle(tabList, 'order', '1'); // Ensure headers come first

    // Individual tab header styles
    const tabs = tabList.querySelectorAll('.tab');
    tabs.forEach((tab: Element, index: number) => {
      const tabElement = tab as HTMLElement;
      this.renderer.setStyle(tabElement, 'width', '100%');
      this.renderer.setStyle(tabElement, 'border-bottom', '1px solid var(--separator-color, #edf1f7)');
      this.renderer.setStyle(tabElement, 'border-right', 'none');
      this.renderer.setStyle(tabElement, 'text-align', 'left');
      this.renderer.setStyle(tabElement, 'justify-content', 'flex-start');
      this.renderer.setStyle(tabElement, 'padding', '0');
      this.renderer.setStyle(tabElement, 'cursor', 'pointer');
      this.renderer.setStyle(tabElement, 'transition', 'all 0.2s ease');
      
      // Remove bottom border from last tab
      if (index === tabs.length - 1) {
        this.renderer.setStyle(tabElement, 'border-bottom', 'none');
      }

      // Find the inner link/button element and style it
      const tabLink = tabElement.querySelector('a') || tabElement.querySelector('button') || tabElement;
      if (tabLink && tabLink !== tabElement) {
        const linkElement = tabLink as HTMLElement;
        this.renderer.setStyle(linkElement, 'padding', '12px 16px');
        this.renderer.setStyle(linkElement, 'display', 'block');
        this.renderer.setStyle(linkElement, 'width', '100%');
        this.renderer.setStyle(linkElement, 'text-decoration', 'none');
        this.renderer.setStyle(linkElement, 'color', 'inherit');
        this.renderer.setStyle(linkElement, 'border', 'none');
        this.renderer.setStyle(linkElement, 'background', 'none');
        this.renderer.setStyle(linkElement, 'text-align', 'left');
      } else {
        // If no inner element, apply padding to the tab itself
        this.renderer.setStyle(tabElement, 'padding', '12px 16px');
      }
      
      // Style active tab
      if (tabElement.classList.contains('active')) {
        this.renderer.setStyle(tabElement, 'border-right', '0 solid var(--primary-color, #3366ff)');
        this.renderer.setStyle(tabElement, 'background-color', 'var(--tabs-active-bg, #ffffff)');
        this.renderer.setStyle(tabElement, 'font-weight', '600');
      } else {
        this.renderer.setStyle(tabElement, 'border-right', 'none');
        this.renderer.setStyle(tabElement, 'background-color', 'transparent');
      }

      // Add hover effects
      this.renderer.listen(tabElement, 'mouseenter', () => {
        if (!tabElement.classList.contains('active')) {
          this.renderer.setStyle(tabElement, 'background-color', 'var(--tabs-hover-bg, #f0f3ff)');
        }
      });

      this.renderer.listen(tabElement, 'mouseleave', () => {
        if (!tabElement.classList.contains('active')) {
          this.renderer.setStyle(tabElement, 'background-color', 'transparent');
        }
      });
    });

    // Content area - ensure proper display based on Nebular's active state
    this.applyContentStyles(tabElements);

    console.log('Applied vertical styles successfully');
  }

  private applyContentStyles(tabElements: NodeListOf<Element>): void {
    tabElements.forEach((nbTab: Element, index: number) => {
      const tabElement = nbTab as HTMLElement;
      
      // Apply basic layout styles
      this.renderer.setStyle(tabElement, 'order', '2');
      this.renderer.setStyle(tabElement, 'padding', '20px');
      this.renderer.setStyle(tabElement, 'overflow', 'auto');
      this.renderer.setStyle(tabElement, 'background-color', 'var(--tabs-content-bg, #ffffff)');
      this.renderer.setStyle(tabElement, 'box-sizing', 'border-box');
      
      // Check if this tab is active based on Nebular's classes or attributes
      const isActive = tabElement.classList.contains('content-active');
      /* const isActive = tabElement.classList.contains('content-active') ||
                      tabElement.classList.contains('active') ||
                      tabElement.hasAttribute('aria-expanded') && tabElement.getAttribute('aria-expanded') === 'true' ||
                      !tabElement.hasAttribute('hidden') && 
                      getComputedStyle(tabElement).display !== 'none' &&
                      tabElement.style.display !== 'none'; */
      
      console.log(`Tab ${index}: active=${isActive}, classes=${tabElement.className}`);
      
      if (isActive) {
        // This tab is active - make it visible and take the full content area
        this.renderer.setStyle(tabElement, 'flex', '1');
        this.renderer.setStyle(tabElement, 'width', '100%');
        this.renderer.setStyle(tabElement, 'height', '100%');
        this.renderer.setStyle(tabElement, 'display', 'block');
        this.renderer.removeAttribute(tabElement, 'hidden');
      } else {
        console.log(`Hiding inactive tab ${index}`);
        // This tab is inactive - hide it completely
        this.renderer.setStyle(tabElement, 'display', 'none');
        this.renderer.setAttribute(tabElement, 'hidden', '');
      }
    });
  }

  private setupMutationObserver(): void {
    this.mutationObserver = new MutationObserver((mutations) => {
      let shouldUpdate = false;
      
      mutations.forEach((mutation) => {
        // Check if tab classes changed (active state)
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const target = mutation.target as HTMLElement;
          if (target.classList.contains('tab')) {
            shouldUpdate = true;
          }
        }
        // Check if tabs were added/removed
        else if (mutation.type === 'childList') {
          shouldUpdate = true;
        }
      });

      if (shouldUpdate) {
        setTimeout(() => {
          const tabset = this.el.nativeElement;
          const tabList = tabset.querySelector('.tabset') || 
                         tabset.querySelector('nb-tab-list') ||
                         tabset.querySelector('.tab-list') ||
                         tabset.querySelector('[role="tablist"]');
          const tabElements = tabset.querySelectorAll('nb-tab');
          
          if (tabList && tabElements.length > 0) {
            this.applyVerticalStyles(tabset, tabList, tabElements);
          }
        }, 10);
      }
    });

    // Observe the entire tabset for changes
    this.mutationObserver.observe(this.el.nativeElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });
  }
}